import { useState, useEffect } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import { useToast } from "@/shared/hooks/use-toast";
import { fetchAuthorizedProperties, fetchPropertiesByOrgId } from "@/features/projects/services/projectsApi";
import { isAdminUser } from "@/shared/lib/adminUids";
import { firebaseAuth } from "@/shared/integrations/firebase/client";

export interface ClientAsset {
  id: string;
  name: string;
  asset_type: string;
  category?: string;          // resolved display name (asset_categories.name, falls back to legacy category text)
  category_id?: string | null;
  property_id: string;
  property_name: string;
  system_id?: string;
  system_type?: string;  // electrical, mechanical, roof, etc.
  system_name?: string;
  manufacturer?: string;
  model_name?: string | null;
  model_number?: string | null;
  serial_number?: string;
  installation_date?: string | null;
  manufacture_date?: string;
  manufacture_date_string?: string | null;
  warranty_expiry?: string;
  status: string;
  condition?: string | null;
  condition_status?: string | null;
  ai_suggested_condition?: string | null;
  estimated_age_years?: number | null;
  age_source?: string | null;
  description?: string | null;
  notes?: string | null;
  zone_id?: string | null;
  location?: string;          // resolved "Location within Store" (project_zones.name)
  nameplate_photo_url?: string | null;
  asset_photo_url?: string | null;
  category_attributes?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export function useClientAssets(
  clientId: string, 
  page: number = 1, 
  pageSize: number = 50,
  searchTerm: string = '',
  statusFilter?: string,
  categoryFilter?: string,
  propertyFilter?: string,
) {
  const [assets, setAssets] = useState<ClientAsset[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  // MVP2-282: Keep previous assets to prevent flicker during search
  const [previousAssets, setPreviousAssets] = useState<ClientAsset[]>([]);

  const fetchAssets = async () => {
    if (!clientId) {
      return;
    }
    
    try {
      // MVP2-282: Save current assets before loading new ones (optimistic UI)
      if (assets.length > 0) {
        setPreviousAssets(assets);
      }
      
      setLoading(true);
      setError(null);
      
      // Get this client's properties.
      //  - Admins: the SELECTED client by organization_id (cross-client switching).
      //  - Client users: their authorized set from the /projects/client endpoint.
      const properties = isAdminUser(firebaseAuth?.currentUser?.uid)
        ? await fetchPropertiesByOrgId<{ id: string; name: string }>(clientId, "id, name")
        : await fetchAuthorizedProperties<{ id: string; name: string }>("id, name");

      if (properties.length === 0) {
        setAssets([]);
        setTotalCount(0);
        return;
      }

      // Create a map for quick property name lookup
      const propertyMap = new Map(properties.map(p => [p.id, p.name]));

      // Apply property filter if specified
      let propertyIds = properties.map(p => p.id);
      if (propertyFilter && propertyFilter !== 'all') {
        propertyIds = [propertyFilter];
      }

      let countQuery = supabase
        .from('property_assets')
        .select(`
          id,
          property_id,
          name,
          description,
          manufacturer,
          model_name,
          model_number,
          serial_number,
          status,
          condition_status,
          estimated_age_years,
          age_source,
          notes,
          zone_id,
          nameplate_photo_url,
          asset_photo_url,
          category_attributes,
          created_at,
          updated_at,
          created_by,
          category_id,
          manufacture_date_string,
          asset_categories!left(
            name
          ),
          project_zones!left(
            name
          )
        `, { count: 'exact', head: false })
        .in('property_id', propertyIds);

      // Apply status filter (uses condition_status column)
      if (statusFilter && statusFilter !== 'all') {
        countQuery = countQuery.eq('condition_status', statusFilter);
      }

      // Apply category filter (uses canonical category_id)
      if (categoryFilter && categoryFilter !== 'all') {
        countQuery = countQuery.eq('category_id', categoryFilter);
      }



      // Apply search filter - FIXED: Removed location column
      if (searchTerm && searchTerm.trim()) {
        const searchLower = `%${searchTerm.trim().toLowerCase()}%`;
        countQuery = countQuery.or(
          `name.ilike.${searchLower},manufacturer.ilike.${searchLower},model_number.ilike.${searchLower},model_name.ilike.${searchLower},serial_number.ilike.${searchLower}`
        );
      }

      // Add pagination
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data: assetsData, error: assetsError, count } = await countQuery
        .range(from, to)
        .order('created_at', { ascending: false });

      if (assetsError) {
        console.error('Assets query error:', assetsError);
        throw assetsError;
      }

      const allAssets = assetsData || [];
      setTotalCount(count || 0);

      // Transform the data to include property names and system info
      const transformedAssets: ClientAsset[] = (allAssets as any[]).map((asset: any) => {
        const categoryData = Array.isArray(asset.asset_categories)
          ? asset.asset_categories[0]
          : asset.asset_categories;
        const zoneData = Array.isArray(asset.project_zones)
          ? asset.project_zones[0]
          : asset.project_zones;

        return {
          ...asset,
          property_name: propertyMap.get(asset.property_id) || 'Unknown Property',
          asset_type: categoryData?.name ?? '',
          category: categoryData?.name ?? undefined,
          manufacture_date: asset.manufacture_date_string ?? undefined,
          location: zoneData?.name ?? undefined,
          category_attributes: asset.category_attributes as unknown as Record<string, unknown> | null,
        };
      });

      setAssets(transformedAssets);
    } catch (error: unknown) {
      console.error('Error fetching client assets:', error);
      const errorMessage = error instanceof Error ? error.message : (typeof error === 'object' && error !== null && 'message' in error) ? String((error as Record<string, unknown>).message) : 'Failed to load assets';
      setError(errorMessage);
      toast({
        title: "Error Loading Assets",
        description: errorMessage,
        variant: "destructive",
      });
      setAssets([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssets();
  }, [clientId, page, pageSize, searchTerm, statusFilter, categoryFilter, propertyFilter]);

  // MVP2-282: Use previous assets during loading to prevent flicker
  const displayAssets = loading && previousAssets.length > 0 ? previousAssets : assets;
  const isRefreshing = loading && previousAssets.length > 0;

  // MVP2-282: Group assets by property (using displayAssets to prevent refresh)
  const assetsByProperty = displayAssets.reduce((acc, asset) => {
    const propertyName = asset.property_name;
    if (!acc[propertyName]) {
      acc[propertyName] = [];
    }
    acc[propertyName].push(asset);
    return acc;
  }, {} as Record<string, ClientAsset[]>);

  // MVP2-282: Group assets by status (using displayAssets to prevent refresh)
  const assetsByStatus = displayAssets.reduce((acc, asset) => {
    const status = asset.status || 'unknown';
    if (!acc[status]) {
      acc[status] = [];
    }
    acc[status].push(asset);
    return acc;
  }, {} as Record<string, ClientAsset[]>);

  // MVP2-282: Group assets by type (using displayAssets to prevent refresh)
  const assetsByType = displayAssets.reduce((acc, asset) => {
    const type = asset.category || asset.system_type || asset.asset_type || 'Uncategorized';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(asset);
    return acc;
  }, {} as Record<string, ClientAsset[]>);

  return {
    assets: displayAssets,
    totalCount,
    currentPage: page,
    totalPages: Math.ceil(totalCount / pageSize),
    hasNextPage: page < Math.ceil(totalCount / pageSize),
    hasPrevPage: page > 1,
    assetsByProperty,
    assetsByStatus,
    assetsByType,
    loading,
    isRefreshing,
    error,
    refetch: fetchAssets,
  };
}
